import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { t } from "i18next";
import MonacoEditor from "@monaco-editor/react";

export default function TabCode({
  firstData,
  secondData,
  thirdData,
  state,
  onCodeChange,
}) {
  const [trainOption, setTrainOption] = useState("options");
  const [options, setOptions] = useState("{}");
  const [pre_process, setPreProcess] = useState("{}");
  const [post_process, setPostProcess] = useState("{}");
  const [formatData, setFormatData] = useState("{}");
  const [jsonValid, setJsonValid] = useState(true);
  const [isUserTyping, setIsUserTyping] = useState(false);

  const handleToggleChange = (e, newTrainOption) => {
    if (newTrainOption !== null) {
      setTrainOption(newTrainOption);
    }
  };

  useEffect(() => {
    const parseOrUseRaw = (data) => {
      if (typeof data === "string") {
        try {
          return JSON.parse(data);
        } catch (e) {
          console.error("JSON parse error:", e);
          return {};
        }
      }
      return data;
    };

    setOptions(parseOrUseRaw(firstData));
    setPreProcess(parseOrUseRaw(secondData));
    setPostProcess(parseOrUseRaw(thirdData));
  }, [firstData, secondData, thirdData]);

  const jsonData = useMemo(() => {
    switch (trainOption) {
      case "options":
        return options;
      case "pre_process":
        return pre_process;
      case "post_process":
        return post_process;
      default:
        return {};
    }
  }, [trainOption, options, pre_process, post_process]);

  useEffect(() => {
    if (!isUserTyping) {
      if (typeof jsonData === "object") {
        setFormatData(JSON.stringify(jsonData, null, 4));
      } else {
        setFormatData(jsonData);
      }
    }
  }, [jsonData, trainOption, isUserTyping]);

  const handleCodeEditorChange = (newValue) => {
    if (newValue !== undefined) {
      setFormatData(newValue);
      setIsUserTyping(true);

      try {
        const parsed = JSON.parse(newValue);
        setJsonValid(true);

        switch (trainOption) {
          case "options":
            setOptions(parsed);
            break;
          case "pre_process":
            setPreProcess(parsed);
            break;
          case "post_process":
            setPostProcess(parsed);
            break;
          default:
            return;
        }

        onCodeChange(trainOption, parsed);
      } catch (e) {
        setJsonValid(false);
        // 오류가 있어도 입력 유지
      }

      // 입력 후 일정 시간 지나면 다시 false로
      clearTimeout(handleCodeEditorChange.timeout);
      handleCodeEditorChange.timeout = setTimeout(() => {
        setIsUserTyping(false);
      }, 5000); // 5초 동안 입력 없으면 타이핑 종료 간주
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <ToggleButtonGroup
        value={trainOption}
        exclusive
        onChange={handleToggleChange}
        sx={{ width: "100%", mb: 2 }}
      >
        <ToggleButton value="options" sx={{ width: "33%" }}>
          {t("Options")}
        </ToggleButton>
        <ToggleButton value="pre_process" sx={{ width: "33%" }}>
          {t("PreProcess")}
        </ToggleButton>
        <ToggleButton value="post_process" sx={{ width: "33%" }}>
          {t("PostProcess")}
        </ToggleButton>
      </ToggleButtonGroup>

      {!jsonValid && (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          {t("Invalid JSON format. Please check your syntax.")}
        </Typography>
      )}

      <MonacoEditor
        height="450px"
        language="json"
        value={formatData}
        options={{
          fontSize: 14,
          automaticLayout: true,
          readOnly: state === "view",
          minimap: {
            enabled: true,
            side: "right",
            size: "proportional",
            scale: 6,
            maxColumn: 200,
          },
        }}
        onChange={handleCodeEditorChange}
      />
    </Box>
  );
}
